import { useCallback, useState, useMemo } from "react";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { Box, Paper } from "@mui/material";

import CustomGrid from "components/lists/CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "components/lists/EnhancedToolbar";
import { buildMovementArticleObservationSummary } from "helpers/movementArticleObservations";
import AddMovementArticleModal from "./AddMovementArticleModal";
import EditMovementModal from "./EditMovementModal";

import { ENTITY } from "constants/entities";
import {
  Article,
  Client,
  Location,
  Movement,
  MovementArticle,
  Store,
  SubLocation,
} from "types";
import { MOVEMENT_TYPES, MovementTypes } from "constants/index";
import useModals from "hooks/useModals";
import { useToast } from "hooks/useToast";

type ArticlesListProps = {
  articles?: Article[];
  movement: Movement;
  onMovementUpdate: <K extends keyof Movement>(
    name: K,
    value: Movement[K]
  ) => void;
  operationType: string;
  locations: Location[];
  hasPalletAutocalculation: boolean;
  hasWeightAutocalculation: boolean;
  stores: Store[];
  rowIndex?: number;
  setRowIndex?: (index: number) => void;
  actionsDisabled?: boolean;
  client: Client | null;
  sublocations?: SubLocation[];
};

const shouldResetObservationPreview = (
  previousArticle: MovementArticle,
  nextArticle: MovementArticle,
) =>
  previousArticle.amount !== nextArticle.amount ||
  previousArticle.article_locations_id !== nextArticle.article_locations_id ||
  previousArticle.article_locations.location_id !==
    nextArticle.article_locations.location_id ||
  (previousArticle.article_locations.lote ?? null) !==
    (nextArticle.article_locations.lote ?? null) ||
  (previousArticle.article_locations.oc ?? null) !==
    (nextArticle.article_locations.oc ?? null) ||
  (previousArticle.article_locations.fecha_ingreso ?? null) !==
    (nextArticle.article_locations.fecha_ingreso ?? null) ||
  (previousArticle.article_locations.fecha_vencimiento ?? null) !==
    (nextArticle.article_locations.fecha_vencimiento ?? null) ||
  (previousArticle.amount_per_container ?? null) !==
    (nextArticle.amount_per_container ?? null);

export default function ArticlesList({
  articles,
  movement,
  onMovementUpdate,
  operationType,
  locations,
  hasPalletAutocalculation,
  hasWeightAutocalculation,
  stores,
  rowIndex = 0,
  setRowIndex,
  actionsDisabled = false,
  client,
  sublocations
}: ArticlesListProps) {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();
  const { showSnackBar } = useToast();

  const handleSetMovements = useCallback(
    (newMovement: MovementArticle) => {
      if (setRowIndex) {
        setRowIndex(rowIndex + 1);
      }
      onMovementUpdate("articles", [newMovement, ...movement.articles]);

      if (hasWeightAutocalculation) {
        onMovementUpdate(
          "weight",
          movement.weight + newMovement.weight * newMovement.amount
        );
      }

      const isPalletIncluded = movement.articles.some(
        (article) =>
          article.article_locations.location_id ===
          newMovement.article_locations.location_id
      );

      if (!isPalletIncluded && hasPalletAutocalculation) {
        onMovementUpdate("pallets", movement.pallets + 1);
      }

      showSnackBar("Se ha agregado el artículo correctamente", "success");
    },
    [
      hasPalletAutocalculation,
      hasWeightAutocalculation,
      movement,
      onMovementUpdate,
      rowIndex,
      setRowIndex,
      showSnackBar,
    ]
  );

  const handleDeleteSelected = useCallback(() => {
    const updatedArticles = movement.articles.filter(
      (movementArticle) => !selected.includes(movementArticle.id)
    );

    selected.forEach((id) => {
      const movementArticle = movement.articles.find(
        (movementArticle) => movementArticle.id === id
      );

      if (movementArticle) {
        const isLocationStillInUse =
          movement.articles.filter(
            (ma) =>
              ma.article_locations.location_id ===
              movementArticle.article_locations.location_id
          ).length > 1;

        if (!isLocationStillInUse && hasPalletAutocalculation) {
          onMovementUpdate("pallets", movement.pallets - 1);
        }

        if (hasWeightAutocalculation) {
          onMovementUpdate(
            "weight",
            movement.weight -
              movementArticle.article_locations.article.weight *
                movementArticle.amount
          );
        }
      }
    });

    onMovementUpdate("articles", updatedArticles);
    setSelected([]);
  }, [
    selected,
    movement,
    onMovementUpdate,
    hasPalletAutocalculation,
    hasWeightAutocalculation,
  ]);

  const handleEdit = useCallback(
    (movementArticleToEdit: MovementArticle) => {
      onMovementUpdate(
        "articles",
        movement.articles.map((movementArticle) => {
          if (movementArticle.id !== movementArticleToEdit.id) {
            return movementArticle;
          }

          if (
            shouldResetObservationPreview(movementArticle, movementArticleToEdit)
          ) {
            return {
              ...movementArticleToEdit,
              stock_unit_selection: undefined,
              observation_preview_units: undefined,
            };
          }

          return movementArticleToEdit;
        })
      );
      setSelected([]);
    },
    [movement.articles, onMovementUpdate]
  );

  const deleteValues = useMemo(
    () =>
      movement.articles
        .filter((movementArticle) => selected.includes(movementArticle.id))
        .map((movementArticle) => movementArticle.description),
    [movement.articles, selected]
  );

  const isToolbarDisabled = useMemo(
    () =>
      actionsDisabled ||
      !movement.client_id ||
      movement.client_id === 0 ||
      !movement.associated_client ||
      !movement.date ||
      !movement.created_by ||
      !movement.updated_by ||
      !movement.client_document,
    [movement, actionsDisabled]
  );

  const gridFields = useMemo(() => {
    const baseFields = gridStructure[ENTITY.MOVEMENT_ARTICLES].fields;
    const movementTypeId = movement.movement_type_id as MovementTypes;
    const extraFields =
      gridStructure[ENTITY.MOVEMENT_ARTICLES].extraFields?.[movementTypeId] || [];

    const allFields = [...baseFields, ...extraFields].sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : Infinity;
      const orderB = b.order !== undefined ? b.order : Infinity;
      return orderA - orderB;
    });

    const visibleFields = allFields.filter((field) => {
      if (!("visibilityFlag" in field)) return true;
      const flag = (field as any).visibilityFlag;
      return client?.[flag as keyof Client];
    });

    if (movementTypeId !== MovementTypes.Discharge) {
      return visibleFields;
    }

    return visibleFields.map((field) => {
      if (field.field !== "observation") {
        return field;
      }

      return {
        ...field,
        editable: false,
        renderCell: (params: { row: MovementArticle }) =>
          buildMovementArticleObservationSummary(params.row),
      };
    });
  }, [movement.movement_type_id, client]);

  return (
    <>
      <AddMovementArticleModal
        open={addModalOpen}
        handleOpen={() => setAddModalOpen(!addModalOpen)}
        movement={movement}
        articles={articles}
        rowIndex={rowIndex}
        handleAddMovementArticle={handleSetMovements}
        operationType={operationType}
        stores={stores}
        locations={
          operationType === MOVEMENT_TYPES.INCOME.label && articles
            ? movement.store_id == null
              ? locations
              : locations.filter(
                  (location) => location.store_id === movement.store_id
                )
            : locations
        }
        sublocations={sublocations || []}
      />
      <DeleteConfirmationModaL
        open={deleteModalOpen}
        handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
        entityName={ENTITY.ARTICLES}
        values={deleteValues}
        handleDelete={handleDeleteSelected}
      />
      {selected.length > 0 && (
        <EditMovementModal
          movement={movement}
          open={editModalOpen}
          handleOpen={() => setEditModalOpen(!editModalOpen)}
          value={
            movement.articles.find(
              (c) => c.id === selected[0]
            ) as MovementArticle
          }
          onEdit={handleEdit}
          locations={locations}
        />
      )}
      <Box width="100%">
        <Paper sx={{ width: "100%", mb: 2 }}>
          <EnhancedTableToolbar
            numSelected={selected.length}
            disabled={isToolbarDisabled}
            handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
            handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
            handleDeleteModalOpen={() => setDeleteModalOpen(!deleteModalOpen)}
          />
          <CustomGrid<MovementArticle>
            structure={{
              ...gridStructure[ENTITY.MOVEMENT_ARTICLES],
              fields: gridFields,
            }}
            rows={movement.articles}
            onEdit={handleEdit}
            selectionModel={selected}
            onRowSelection={setSelected}
            checkboxSelection={!actionsDisabled}
          />
        </Paper>
      </Box>
    </>
  );
}
